// Экран 3 из ТЗ раздел 3.3: моя подписка (тариф, статус, дата окончания). Устройства (экран 4,
// привязка/отвязка/ссылки на установку) объединены СЮДА ЖЕ по запросу пользователя 2026-08-20 —
// изначально это был отдельный маршрут/вкладка, но при 7 вкладках нижний таббар не помещался на
// экран; "Подписка" и "Устройства" тематически об одном и том же, отдельная вкладка была лишней.
//
// subscription.bundle_partner (2026-08-20, решает docs/OPEN_QUESTIONS.md "несколько активных
// подписок на пользователя") — вторая половина пары "Расширенный" (Mobile+Premium). Привязка/
// отвязка устройств на этой половине сознательно не поддержана на этом экране — текущий
// attach/detach API неявно берёт "подписку пользователя" (см. app/api/routes/miniapp/devices.py),
// а не конкретный subscription_id; полноценное управление обеими половинами пары — отдельная
// задача. Скачивание дистрибутива — read-only действие, ЕСТЬ и для этой половины.
//
// Уточнения пользователя 2026-08-20 (второй раунд правок раздела "Устройства"):
// - Отдельный блок "Скачать" (Android/Компьютер) УБРАН — дублировал кнопку у каждого устройства
//   ("Ссылки на установку", сокращена до "Скачать"), два способа получить одну и ту же ссылку
//   были лишними.
// - Онлайн/офлайн — только цветной кружок перед именем (зелёный/красный), без отдельного текста
//   "онлайн"/"офлайн" (есть в title-атрибуте для доступности, но не отображается как текст).
// - Кружок + имя + кнопка "Скачать" — один ряд (flex), не расползаются по отдельным строкам.
// - Сообщение "Достигнут лимит устройств тарифа" убрано — форма привязки просто не показывается
//   при достижении лимита, без замены текстом.
// - У блока "Компьютер" (bundle_partner) больше не дублируются "Тариф"/"Действует до" — эта
//   информация уже показана в самом начале экрана (для пары "Расширенный" срок общий).
// - Приписка "все дистрибутивы — архитектуры x64" — везде, где показываются ссылки на установку.
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  attachDevice,
  detachDevice,
  fetchDeviceInstallPackages,
  fetchSubscription,
  type Device,
  type InstallPackage,
  type SubscriptionSummary,
} from "../api/client";
import { useSubscription } from "../hooks/useSubscription";

const STATUS_KEYS = {
  active: "status_active",
  expiring: "status_expiring",
  expired: "status_expired",
  cancelled: "status_cancelled",
} as const;

// Человекочитаемые подписи для кодов ОС от AV-Desk — тот же список, что и в
// app/bot/formatting.py::_OS_LABELS (backend), продублирован здесь: фронт не может импортировать
// backend-код, а список маленький и стабильный (коды ОС определяет AV-Desk, не мы).
const OS_LABELS: Record<string, string> = {
  windows: "Windows",
  osx: "macOS",
  linux: "Linux",
  android: "Android",
  android4: "Android",
};

function osLabel(os: string): string {
  return OS_LABELS[os] ?? os;
}

export function MySubscriptionScreen() {
  const { t, i18n } = useTranslation();
  const { subscription, loading, error, setSubscription, setError } = useSubscription();
  // toLocaleDateString() без аргумента locale берёт локаль окружения (в Telegram WebView — не
  // обязательно совпадает с языком интерфейса бота), из-за чего дата рендерилась как "26/08/2026"
  // вместо привычного "26.08.2026" — найдено живым тестированием 2026-08-20. Явно привязываем к
  // текущему языку i18n (ru/en), а не к окружению.
  const dateLocale = i18n.language === "en" ? "en-US" : "ru-RU";

  const [name, setName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [newPackages, setNewPackages] = useState<InstallPackage[] | null>(null);
  const [packagesByDevice, setPackagesByDevice] = useState<Record<number, InstallPackage[]>>({});
  const [busyDeviceId, setBusyDeviceId] = useState<number | null>(null);

  const refresh = () =>
    fetchSubscription()
      .then(setSubscription)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)));

  const handleAttach = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setAttaching(true);
    setError(null);
    setNewPackages(null);
    attachDevice(trimmed)
      .then(({ install_packages }) => {
        setName("");
        setNewPackages(install_packages);
        return refresh();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setAttaching(false));
  };

  const handleShowLinks = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    fetchDeviceInstallPackages(deviceId)
      .then((packages) => setPackagesByDevice((prev) => ({ ...prev, [deviceId]: packages })))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const handleDetach = (deviceId: number) => {
    setBusyDeviceId(deviceId);
    setError(null);
    detachDevice(deviceId)
      .then(() => refresh())
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setBusyDeviceId(null));
  };

  const renderPackages = (packages: InstallPackage[]) =>
    packages.length === 0 ? (
      <p>{t("devices_links_pending")}</p>
    ) : (
      <>
        <p className="packages-note">{t("devices_packages_x64_note")}</p>
        <ul>
          {packages.map((pkg) => (
            <li key={pkg.os}>
              <a href={pkg.url}>{osLabel(pkg.os)}</a>
            </li>
          ))}
        </ul>
      </>
    );

  const renderDeviceList = (devices: Device[], options: { readOnly: boolean }) => (
    <ul>
      {devices.map((device) => (
        <li key={device.id}>
          <div className="device-row">
            <span
              className={`status-dot ${device.is_online ? "online" : "offline"}`}
              title={device.is_online ? t("devices_status_online") : t("devices_status_offline")}
            />
            <span className="device-name">
              {device.name}
              {device.is_primary ? ` — ${t("devices_primary")}` : ""}
            </span>
            <button
              type="button"
              onClick={() => handleShowLinks(device.id)}
              disabled={busyDeviceId === device.id}
            >
              {t("devices_show_links")}
            </button>
          </div>
          {!options.readOnly && !device.is_primary && (
            <button
              type="button"
              onClick={() => handleDetach(device.id)}
              disabled={busyDeviceId === device.id}
            >
              {t("devices_detach")}
            </button>
          )}
          {packagesByDevice[device.id] && renderPackages(packagesByDevice[device.id])}
        </li>
      ))}
    </ul>
  );

  const renderPartnerBlock = (partner: SubscriptionSummary) => (
    <div>
      <h3>{t("devices_group_computer")}</h3>
      {partner.devices.length === 0 ? (
        <p>{t("devices_empty")}</p>
      ) : (
        renderDeviceList(partner.devices, { readOnly: true })
      )}
    </div>
  );

  return (
    <div>
      <h1>{t("subscription_title")}</h1>
      {error && <p role="alert">{error}</p>}
      {!error && loading && <p>{t("loading")}</p>}
      {!error && !loading && subscription === null && <p>{t("subscription_none")}</p>}
      {subscription && (
        <div>
          <p>
            {t("subscription_plan")}:{" "}
            <strong>
              {subscription.plan.title}
              {subscription.plan.is_bundle ? "*" : ""}
            </strong>
          </p>
          <p>
            {t("subscription_status")}: {t(STATUS_KEYS[subscription.status])}
          </p>
          <p>
            {t("subscription_expires_at")}:{" "}
            {new Date(subscription.expires_at).toLocaleDateString(dateLocale)}
          </p>

          <h2>{t("devices_title")}</h2>
          {subscription.bundle_partner && <h3>{t("devices_group_mobile")}</h3>}
          {subscription.devices.length === 0 && <p>{t("devices_empty")}</p>}
          {renderDeviceList(subscription.devices, { readOnly: false })}

          {subscription.devices.length < subscription.device_limit && (
            <form onSubmit={handleAttach}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("devices_name_placeholder")}
              />
              <button type="submit" disabled={attaching || !name.trim()}>
                {attaching ? t("loading") : t("devices_attach")}
              </button>
            </form>
          )}

          {newPackages && (
            <div>
              <p>{t("devices_new_links_title")}</p>
              {renderPackages(newPackages)}
            </div>
          )}

          {subscription.bundle_partner && renderPartnerBlock(subscription.bundle_partner)}

          {subscription.plan.is_bundle && (
            <p className="footnote">* {t("subscription_bundle_footnote")}</p>
          )}
        </div>
      )}
    </div>
  );
}
